import { useEvent } from '@emerald-works/react-event-bus-client'
import { CloseTwoTone } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, Popper, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { onboardingSlice } from '../../../reducers'
import { routes } from '../../../router/routes'
import { HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE, useTrackHomeEnterpriseEvent } from '../hooks/useTrackHomeEnterpriseEvent'

export const useStyles = makeStyles(({ palette, spacing, breakpoints }) => ({
  popper: {
    background: palette.primary.light,
    zIndex: 1201,
    color: '#000',
    width: 375,
    maxWidth: 375,
    borderRadius: 5,
    position: 'absolute!important', // override inline popper position
    inset: '0px auto auto 0px',
    margin: 0,
    transform: 'translate(-137px, 44px)'
  },
  arrow: {
    position: 'absolute',
    top: -10,
    left: 'calc(50% - 10px)',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: `10px solid ${palette.primary.light}`
  },
  closeButton: {
    marginTop: 0,
    position: 'absolute',
    top: '-5px',
    right: '-5px',
    color: '#000'
  },
  closeIcon: {
    fontSize: '1rem'
  },
  title: {
    fontWeight: 700,
    marginBottom: spacing(1)
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left'
  },
  dismiss: {
    padding: 0,
    paddingTop: spacing(1),
    minWidth: 0,
    color: '#000',
    textDecoration: 'underline'
  }
}))

export const HomeOnboarding = ({ anchorElm }) => {
  const showHomeEnterprisePopper = useSelector(onboardingSlice.selectors.selectHomeEnterprisePopoverOpen)
  const { trackOnboardingEvent } = useTrackHomeEnterpriseEvent()
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const isOnHomePage = location.pathname === routes.home.path
  const isOpen = (showHomeEnterprisePopper && !isOnHomePage)
  const [hasTrackedBannerShown, setHasTrackedBannerShown] = React.useState(false)

  const [onboardingTrackEvent] = useEvent([
    onboardingSlice.eventBus.homeEnterpriseTrackEvent
  ])

  const onClose = () => {
    onboardingTrackEvent.trigger({
      type: 'dismissHomeEnterprise'
    })
    trackOnboardingEvent(HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE.BANNER_CLICKED, {
      ctaType: 'dismiss'
    })
  }

  const onRemindMeLater = () => {
    onboardingTrackEvent.trigger({
      type: 'remindMeLaterHomeEnterprise'
    })
    trackOnboardingEvent(HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE.BANNER_CLICKED, {
      ctaType: 'remind_me_later'
    })
  }

  const onNavigateToHome = () => {
    trackOnboardingEvent(HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE.BANNER_CLICKED, {
      ctaType: 'take_a_look'
    })
    navigate(routes.home.path)
  }

  useEffect(() => {
    if (isOpen && !hasTrackedBannerShown) {
      trackOnboardingEvent(HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE.BANNER_SHOWN)
      setHasTrackedBannerShown(true)
    }
  }, [isOpen, trackOnboardingEvent, hasTrackedBannerShown])

  return (
    <Popper
      className={classes.popper}
      open={isOpen}
      anchorEl={anchorElm}
      disablePortal
      placement='bottom'
      modifiers={[
        {
          name: 'arrow',
          enabled: true,
          options: {
            element: anchorElm
          }
        },
        {
          name: 'offset',
          options: {
            offset: [0, 10]
          }
        }
      ]}
    >
      <div className={classes.arrow} />
      <Paper sx={{ padding: 2, bgcolor: 'primary.light' }} elevation={2}>
        <IconButton
          aria-label='Close home onboarding menu'
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseTwoTone className={classes.closeIcon} />
        </IconButton>
        <Box
          className={classes.content}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'middle'
          }}
        >
          <Typography className={classes.title} variant='subtitle1'> Your new homepage is here</Typography>
          <Typography variant='body'>Tailored learning at a glance. Tap the logo to return anytime.</Typography>
          <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%' marginTop={2}>
            <Button className={classes.dismiss} onClick={onRemindMeLater} variant='text'>Remind me later</Button>
            <Button onClick={onNavigateToHome} variant='outlined'>Take a look</Button>
          </Box>
        </Box>
      </Paper>
    </Popper>
  )
}
